import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, SkipForward, ChevronRight, Users, Trophy, QrCode, StopCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_WS = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
const API = process.env.REACT_APP_BACKEND_URL;

function TimerBar({ timer, timeLeft }) {
  if (!timer || timer === 0) return null;
  const pct = (timeLeft / timer) * 100;
  const color = pct > 50 ? '#06d6a0' : pct > 25 ? '#ffd93d' : '#ff6b6b';
  return (
    <div className="w-full h-4 bg-white/10 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function QuizPresent() {
  const { pin } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('waiting'); // waiting, question, reveal, leaderboard, ended
  const [participants, setParticipants] = useState([]);
  const [currentQ, setCurrentQ] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timer, setTimer] = useState(20);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [finalRankings, setFinalRankings] = useState([]);
  const [quizTitle, setQuizTitle] = useState('');
  const [revealData, setRevealData] = useState(null);
  const ws = useRef(null);
  const timerRef = useRef(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  // Refs pour éviter les stale closures dans les handlers WebSocket
  const handleMessageRef = useRef(null);
  const phaseRef = useRef(phase);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    axios.get(`${API}/api/quiz/sessions/${pin}`).then(r => {
      setSessionInfo(r.data);
      setQuizTitle(r.data.quiz_title || '');
      setParticipants(r.data.participants || []);
    }).catch(() => {});
  }, [pin]);

  const connect = useCallback(() => {
    ws.current = new WebSocket(`${API_WS}/api/quiz/ws/${pin}/host`);
    ws.current.onopen = () => toast.success('Connexion établie');
    // Utilise la ref pour toujours appeler la dernière version de handleMessage
    ws.current.onmessage = (e) => handleMessageRef.current(JSON.parse(e.data));
    ws.current.onerror = () => toast.error('Connexion perdue');
    ws.current.onclose = () => {
      // Ne reconnecte pas si la session est terminée
      if (phaseRef.current !== 'ended') {
        setTimeout(connect, 3000);
      }
    };
  }, [pin]);

  useEffect(() => {
    connect();
    return () => {
      clearInterval(timerRef.current);
      if (ws.current) ws.current.close();
    };
  }, [connect]);

  const send = (msg) => {
    if (ws.current?.readyState === 1) ws.current.send(JSON.stringify(msg));
  };

  const handleMessage = useCallback((msg) => {
    switch (msg.type) {
      case 'host_welcome':
        setQuizTitle(msg.quiz_title || '');
        setParticipants(msg.participants || []);
        setTotalQuestions(msg.total_questions || 0);
        if (msg.status !== 'waiting') setPhase(msg.status);
        break;
      case 'waiting_room':
      case 'participant_joined':
        setParticipants(msg.participants || []);
        break;
      case 'question':
        clearInterval(timerRef.current);
        setCurrentQ(msg.question);
        setQuestionIndex(msg.index || 0);
        setTotalQuestions(msg.total || totalQuestions);
        setAnsweredCount(0);
        setRevealData(null);
        setTimer(msg.timer || 20);
        setTimeLeft(msg.timer || 20);
        setPhase('question');
        if (msg.timer > 0) {
          timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
              if (prev <= 1) { clearInterval(timerRef.current); return 0; }
              return prev - 1;
            });
          }, 1000);
        }
        break;
      case 'answer_count':
        setAnsweredCount(msg.answered || 0);
        break;
      case 'reveal':
        setRevealData(msg);
        setPhase('reveal');
        clearInterval(timerRef.current);
        break;
      case 'leaderboard':
        setLeaderboard(msg.rankings || []);
        setPhase('leaderboard');
        break;
      case 'session_ended':
        clearInterval(timerRef.current);
        setFinalRankings(msg.final_rankings || []);
        setPhase('ended');
        break;
      default:
        break;
    }
  }, [totalQuestions]);

  // Maintient la ref à jour — le handler WS utilise toujours la version fraîche
  useEffect(() => { handleMessageRef.current = handleMessage; }, [handleMessage]);

  const participantCount = participants.length;
  const sessionUrl = `${window.location.origin}/quiz/${pin}/live`;

  return (
    <div className="min-h-screen bg-[#0f0e17] flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-4">
          <span className="text-[#ff6b6b] font-mono text-2xl font-bold tracking-widest">{pin}</span>
          <span className="text-[#a0a0ab] text-sm">{quizTitle}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-sm text-[#a0a0ab]">
            <Users size={14} /> {participantCount}
          </span>
          {phase === 'question' && (
            <span className="flex items-center gap-1 text-sm text-[#a0a0ab]">
              <Eye size={13} /> {answeredCount}/{participantCount}
            </span>
          )}
          {phase !== 'ended' && (
            <button onClick={() => send({ action: 'end' })} className="flex items-center gap-1 text-xs text-[#ff6b6b] hover:text-white border border-[#ff6b6b]/30 px-2 py-1 rounded-lg transition-colors">
              <StopCircle size={12} /> Terminer
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-6">
        {phase === 'waiting' && (
          <div className="text-center max-w-md">
            <h1 className="text-3xl font-heading font-bold text-white mb-2">{quizTitle}</h1>
            <p className="text-[#a0a0ab] mb-6">En attente des joueurs...</p>
            <div className="bg-[#ff6b6b]/10 border border-[#ff6b6b]/30 rounded-2xl p-4 mb-6">
              <p className="text-xs text-[#a0a0ab] mb-1">Code PIN</p>
              <p className="text-5xl font-mono font-bold text-[#ff6b6b] tracking-widest">{pin}</p>
              <p className="text-xs text-[#a0a0ab] mt-2 truncate">{sessionUrl}</p>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {participants.map(p => (
                <span key={p.participant_id} className="text-sm bg-white/10 text-white px-3 py-1 rounded-full">{p.nickname}</span>
              ))}
            </div>
            <p className="text-[#a0a0ab] text-sm mb-4">{participantCount} joueur{participantCount !== 1 ? 's' : ''} connecté{participantCount !== 1 ? 's' : ''}</p>
            <button onClick={() => send({ action: 'start' })} disabled={participantCount === 0}
              className="btn-primary px-8 py-4 text-lg flex items-center gap-2 mx-auto disabled:opacity-50" data-testid="start-session-btn">
              <Play size={20} /> Démarrer
            </button>
          </div>
        )}

        {phase === 'question' && currentQ && (
          <div className="w-full max-w-4xl">
            <div className="flex items-center justify-between mb-4 text-sm text-[#a0a0ab]">
              <span>Question {questionIndex + 1} / {totalQuestions}</span>
              <span className="text-[#ffd93d] font-mono text-3xl font-bold">{timeLeft > 0 ? timeLeft : '—'}</span>
              <span>{answeredCount} / {participantCount} ont répondu</span>
            </div>
            <TimerBar timer={timer} timeLeft={timeLeft} />

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 my-4 text-center">
              {currentQ.emoji && <div className="text-5xl mb-3">{currentQ.emoji}</div>}
              <p className="text-white text-2xl font-bold leading-tight">{currentQ.text}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              {(currentQ.options || []).map((opt, i) => {
                const COLORS = ['bg-[#ff6b6b]/20 border-[#ff6b6b]/40', 'bg-[#06d6a0]/20 border-[#06d6a0]/40', 'bg-[#ffd93d]/20 border-[#ffd93d]/40', 'bg-purple-500/20 border-purple-500/40'];
                const LABELS = ['▲', '●', '★', '■'];
                return (
                  <div key={i} className={`${COLORS[i % COLORS.length]} border rounded-xl p-4 flex items-center gap-3`}>
                    <span className="text-lg font-bold text-[#a0a0ab]">{LABELS[i]}</span>
                    <span className="text-white text-base">{opt}</span>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 justify-center">
              <button onClick={() => send({ action: 'reveal' })} className="btn-primary px-6 py-2.5 text-sm flex items-center gap-2">
                Révéler <Eye size={14} />
              </button>
              <button onClick={() => send({ action: 'next' })} className="bg-white/10 hover:bg-white/20 text-white px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 transition-colors">
                Suivant <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {phase === 'reveal' && currentQ && revealData && (
          <div className="w-full max-w-4xl">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-4 text-center">
              <p className="text-white text-xl font-bold mb-3">{currentQ.text}</p>
              {revealData.explanation && <p className="text-[#a0a0ab] text-sm">{revealData.explanation}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {(currentQ.options || []).map((opt, i) => {
                const isCorrect = (revealData.correct_answers || []).includes(i);
                return (
                  <div key={i} className={`rounded-xl p-4 border flex items-center gap-3 ${isCorrect ? 'bg-[#06d6a0]/20 border-[#06d6a0]' : 'bg-white/5 border-white/10 opacity-50'}`}>
                    <span className="text-lg">{isCorrect ? '✓' : '○'}</span>
                    <span className="text-white">{opt}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 justify-center">
              <button onClick={() => send({ action: 'leaderboard' })} className="btn-primary px-6 py-2.5 text-sm flex items-center gap-2">
                <Trophy size={14} /> Classement
              </button>
              <button onClick={() => send({ action: 'next' })} className="bg-white/10 hover:bg-white/20 text-white px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 transition-colors">
                Suivant <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {phase === 'leaderboard' && (
          <div className="w-full max-w-lg text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Trophy size={24} className="text-[#ffd93d]" />
              <h2 className="text-2xl font-bold text-white">Classement</h2>
            </div>
            <div className="space-y-2 mb-6">
              {leaderboard.slice(0, 5).map((r, i) => (
                <div key={r.participant_id} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl px-5 py-3"
                  style={{ transform: `scale(${1 - i * 0.03})`, opacity: 1 - i * 0.1 }}>
                  <span className="text-2xl font-bold text-[#ffd93d] w-8">{['🥇', '🥈', '🥉', '4', '5'][i]}</span>
                  <span className="flex-1 text-white font-semibold text-left">{r.nickname}</span>
                  <span className="text-[#ff6b6b] font-bold text-lg">{r.score.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <button onClick={() => send({ action: 'next' })} className="btn-primary px-8 py-3 text-sm flex items-center gap-2 mx-auto">
              Question suivante <ChevronRight size={14} />
            </button>
          </div>
        )}

        {phase === 'ended' && (
          <div className="text-center max-w-lg w-full">
            <div className="text-5xl mb-4">🏆</div>
            <h1 className="text-3xl font-bold text-white mb-1">Session terminée !</h1>
            <p className="text-[#a0a0ab] mb-6">{quizTitle}</p>
            {finalRankings[0] && (
              <div className="bg-[#ffd93d]/10 border border-[#ffd93d]/30 rounded-2xl p-5 mb-4">
                <p className="text-[#ffd93d] text-sm mb-1">Vainqueur</p>
                <p className="text-white font-bold text-2xl">{finalRankings[0].nickname}</p>
                <p className="text-[#ffd93d] font-bold text-xl">{finalRankings[0].score?.toLocaleString()} pts</p>
              </div>
            )}
            <div className="space-y-2 mb-6">
              {finalRankings.slice(0, 8).map((r, i) => (
                <div key={r.participant_id} className="flex items-center gap-3 bg-white/5 border border-white/5 rounded-xl px-4 py-2">
                  <span className="text-[#ffd93d] font-bold w-6">{i + 1}</span>
                  <span className="flex-1 text-white text-sm">{r.nickname}</span>
                  <span className="text-[#ff6b6b] font-bold text-sm">{r.score?.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate('/quiz')} className="btn-primary px-8 py-3 text-sm">Retour aux quizzes</button>
          </div>
        )}
      </div>
    </div>
  );
}
